import { uploadImage } from "@/lib/api";

// Downscale + re-encode to JPEG in the browser before upload. This keeps uploads
// small (fast, avoids the server size limit) and normalizes odd formats (HEIC,
// large PNG screenshots) into something that always uploads. Falls back to the
// original file if the image can't be processed.
async function compressImage(file, maxDim = 1920, quality = 0.85) {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  if (file.type === "image/gif") return file; // keep animation
  try {
    const dataUrl = await new Promise((res, rej) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.onerror = rej;
      fr.readAsDataURL(file);
    });
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = dataUrl;
    });
    let { width, height } = img;
    if (!width || !height) return file;
    const scale = Math.min(1, maxDim / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob) return file;
    // If compression didn't actually help a small file, keep whichever is smaller.
    if (blob.size >= file.size && file.size < 1_500_000) return file;
    const name = (file.name || "photo").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  }
}

// Opens the OS file picker, compresses + uploads the selected image(s), and
// returns an array of media urls. Resolves to [] when the picker is cancelled
// (critical: otherwise the caller's "uploading" state would hang forever), or
// { error } if an upload fails.
export function pickAndUpload({ multiple = false } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = multiple;
    input.style.position = "fixed";
    input.style.left = "-9999px";
    document.body.appendChild(input);

    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("focus", onFocus);
      try { document.body.removeChild(input); } catch {}
      resolve(value);
    };

    // Detect a cancelled picker: the window regains focus but no file was chosen.
    const onFocus = () => {
      setTimeout(() => {
        if (!settled && (!input.files || input.files.length === 0)) finish([]);
      }, 600);
    };

    input.onchange = async () => {
      const files = Array.from(input.files || []);
      if (files.length === 0) return finish([]);
      try {
        const urls = [];
        for (const f of files) {
          const compressed = await compressImage(f);
          const url = await uploadImage(compressed);
          urls.push(url);
        }
        finish(urls);
      } catch (e) {
        finish({ error: e?.message || "Upload failed" });
      }
    };

    window.addEventListener("focus", onFocus);
    input.click();
  });
}
